import { TASK_STATUS, utils } from '@seedplanner/shared-utils';

export interface TaskGroup<T extends TaskGroupDataNeeded = TaskGroupDataNeeded> {
  readonly tasks: T[];
  readonly traysNotRounded: number;
  readonly isCompleted: boolean;
}

interface TaskGroupDataNeeded {
  readonly id: string;
  readonly crop?: {
    readonly id: string;
    readonly crop_name: string;
  } | null;
  readonly task_name: string;
  readonly task_time: number;
  readonly task_status: string;
  readonly task_completed_time?: number | null;
  readonly planting?: {
    readonly id: string;
    readonly trays_rounded: string;
  } | null;
}

export const groupTasksWithStatus = <T extends TaskGroupDataNeeded>(tasks: readonly T[]) => {
  const groupedTasks = tasks.reduce((acc, task) => {
    // If it has a crop ID then group by crop ID + task name + task time
    // If no crop ID then group by task ID (that is to say, don't group)
    const cropId = task.crop?.id ?? -1;
    const key =
      cropId === -1 ? task.id : `${cropId}-${task.task_name}-${task.task_time}-${task.task_completed_time ?? 0}`;
    const maybeCurrentGroup = acc.get(key);
    if (maybeCurrentGroup === undefined) {
      acc.set(key, {
        isCompleted: task.task_status === TASK_STATUS.COMPLETED,
        tasks: [task],
        traysNotRounded:
          task.planting?.trays_rounded !== undefined ? Number.parseFloat(task.planting.trays_rounded) : 0,
      });
    } else {
      const existingStatus = maybeCurrentGroup.isCompleted;
      const existingTasks = maybeCurrentGroup.tasks;
      const existingTraysNotRounded = maybeCurrentGroup.traysNotRounded;
      // If status is planned or in progress, then keep isCompleted will be false
      // If status is completed or cancelled, then isCompleted is true only if the existing status is true as well
      const isCompleted = existingStatus && task.task_status === TASK_STATUS.COMPLETED;
      acc.set(key, {
        isCompleted,
        tasks: [task, ...existingTasks],
        traysNotRounded: utils.addTraysRaw(
          existingTraysNotRounded,
          task.planting?.trays_rounded !== undefined ? Number.parseFloat(task.planting.trays_rounded) : 0,
        ),
      });
    }

    return acc;
  }, new Map<string, TaskGroup<T>>());

  return groupedTasks;
};
